import CVApplicationViewComponent from 'components/cvApplications/view/CVApplicationViewComponent';
import HeadMetadata from 'components/shared/HeadMetadata';
import Spinner from 'components/shared/Spinner';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { useI18n } from 'hooks/useI18n';
import { PublicManfrediteJobApplication } from 'interfaces/manfredite.interface';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import ManfrediteJobApplicationService from 'services/manfredite-job-application.service';
import { DEFAULT_LOCALE } from 'services/multilingual.service';
import { APP_URLS, getAppUrl, getNotFoundUrl } from 'utils/app-urls';
import { createGetServerSideProps } from 'utils/ssr';

type CVApplicationViewPageProps = {
  slug: string;
};

const CVApplicationViewPage: React.FC<CVApplicationViewPageProps> = ({
  slug
}) => {
  const { t } = useI18n('cv-applications-view');
  const { push, locale } = useRouter();

  const { user, loadingUser } = useContext(LoggedUserContext);

  const [data, setData] = useState<PublicManfrediteJobApplication>();

  useEffect(() => {
    (async () => {
      if (!loadingUser) {
        try {
          const jobApp = await ManfrediteJobApplicationService.getBySlug(
            slug,
            user?.id
          );

          setData(jobApp);
        } catch (ex) {
          push(getAppUrl(APP_URLS.cvApplicationNoPermission, locale));
        }
      }
    })();
  }, [slug, locale, push, user?.id, loadingUser]);

  return (
    <>
      <HeadMetadata title={t('cvApplicationsView.pageTitle')} />

      {!data && <Spinner />}

      {data && (
        <CVApplicationViewComponent
          slug={slug}
          jobApplicationResume={data.jobApplicationResume}
          termsAndConditionsAccepted={!!data.publicManfredite}
          cvApplication={data.jobApplication}
          publicManfredite={data.publicManfredite}
        />
      )}
    </>
  );
};

export default CVApplicationViewPage;

export const getServerSideProps: GetServerSideProps<CVApplicationViewPageProps> =
  createGetServerSideProps(async ({ params, locale }) => {
    const redirect404 = {
      redirect: {
        destination: getNotFoundUrl(locale),
        permanent: false
      }
    };

    const cvApplicationSlug = params?.slug?.toString();

    if (!cvApplicationSlug) {
      return redirect404;
    }

    const transProps = await serverSideTranslations(locale || DEFAULT_LOCALE, [
      'common',
      'topbar',
      'footer',
      'cv-applications-view',
      'publicProfile'
    ]);

    return {
      props: {
        ...transProps,
        slug: cvApplicationSlug
      }
    };
  });
